import { TextInput } from '../../../components/FormUtils/TextInput'
import { useAuth } from 'context/authentication/useAuth'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import to from 'await-to-js'
import { handleFormError } from '../../../utils/handleFormError'
import { ErrorText } from '../../../components/FormUtils/ErrorText'
import FbButton from '../../../components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { publicFeatureFlags } from '../../../utils/publicFeatureFlags'
import { useEffect, useState } from 'react'
import { HiOutlineMail } from 'react-icons/hi'
import { useCooldownTimer } from 'hooks/useCooldownTimer'

const loginFormShape = z.object({
  email: z.string().email(),
  password: z.string(),
  non_field_errors: z.string().optional(),
})

function Login() {
  const { login, resendVerify } = useAuth()
  const [enableSSO, setEnableSSO] = useState(false)
  const [showVerifyEmail, setShowVerifyEmail] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)
  const { cooldownSeconds, startCooldown, isOnCooldown } = useCooldownTimer()

  useEffect(() => {
    // Check if SSO is enabled using public feature flags
    const checkSSO = async () => {
      await publicFeatureFlags.loadFlags()
      const ssoEnabled = publicFeatureFlags.isEnabled('enable_sso')
      setEnableSSO(ssoEnabled)
    }
    checkSSO()
  }, [])

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    getValues,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      non_field_errors: '',
    },
    resolver: zodResolver(loginFormShape),
  })

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(login(values.email, values.password))
      if (err) {
        // Check if error is email not verified before handling
        const errorData = (err as any)?.response?.data
        const nonFieldErrors = errorData?.non_field_errors
        const errorMsg = Array.isArray(nonFieldErrors)
          ? nonFieldErrors[0]
          : nonFieldErrors

        if (errorMsg === 'E-mail is not verified.') {
          setShowVerifyEmail(true)
          return
        }

        handleFormError(err, setError)
      }
    },
    (errors) => console.warn(errors)
  )

  const handleResendVerification = async () => {
    const email = getValues('email')
    const [err] = await to(resendVerify(email))
    if (!err) {
      setVerificationSent(true)
      startCooldown()
    }
    handleFormError(err, setError)
  }

  const handleBackToLogin = () => {
    setShowVerifyEmail(false)
    setVerificationSent(false)
    clearErrors()
  }

  // Email not verified state
  if (showVerifyEmail) {
    return (
      <div className="w-96 flex-shrink-0">
        <div className="text-[22px] font-light text-new-company-gray-500">
          Welcome to{' '}
          <span className="text-new-company-green-dark font-semibold">
            First Bite
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
            <HiOutlineMail className="w-8 h-8 text-amber-600" />
          </div>

          <h1 className="text-[20px] font-semibold mb-2">
            {verificationSent ? 'Check your inbox' : 'Verify your email'}
          </h1>

          <p className="text-gray-600 mb-6">
            {verificationSent
              ? "We've sent a new verification email. Please click the link in the email to verify your account."
              : 'Your email address has not been verified yet. Please verify your email to log in.'}
          </p>

          {verificationSent && (
            <div className="w-full p-4 bg-gray-50 rounded-lg mb-6">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Tip:</span> If you don&apos;t see
                the email, check your spam or junk folder.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3 w-full">
            <FbButton
              onClick={handleResendVerification}
              disabled={isOnCooldown}
              className="w-full"
            >
              {isOnCooldown
                ? `Resend available in ${cooldownSeconds}s`
                : verificationSent
                  ? 'Resend verification email'
                  : 'Send verification email'}
            </FbButton>

            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-new-company-green-dark hover:underline"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Normal login form
  return (
    <form onSubmit={onSubmit} className="w-96 flex-shrink-0">
      <div className="text-[22px] font-light text-new-company-gray-500">
        Welcome to{' '}
        <span className="text-new-company-green-dark font-semibold">
          First Bite
        </span>
      </div>
      <h1 className="text-[18px] mb-2 mt-4">Log In</h1>
      <div className="mb-4">
        {"Don't have an account yet? "}
        <FbLink to="/signup">Get Started</FbLink>
      </div>
      <TextInput control={control} name="email" label="Email" />
      <TextInput
        type="password"
        control={control}
        name="password"
        label="Password"
        className="mb-2"
      />
      <FbLink to="/reset-password">Forgot Password?</FbLink>
      <FbButton className="w-full mt-4" type="submit" loading={isSubmitting}>
        Log In
      </FbButton>

      {errors?.non_field_errors &&
        errors.non_field_errors.message !== 'E-mail is not verified.' && (
          <ErrorText control={control} name={'non_field_errors'} />
        )}

      {enableSSO && (
        <>
          <div className="flex items-center my-4">
            <div className="flex-1 h-px bg-gray-300"></div>
            <span className="px-3 text-gray-600 text-xs">or</span>
            <div className="flex-1 h-px bg-gray-300"></div>
          </div>

          <div className="flex justify-center mt-4">
            <FbLink to="/sso-login" className="w-full no-underline">
              <button
                type="button"
                className="w-full px-4 py-2.5 bg-gray-100 border border-gray-300 rounded text-sm text-gray-800 cursor-pointer transition-colors duration-200 hover:bg-gray-200"
              >
                Sign in with SSO
              </button>
            </FbLink>
          </div>
        </>
      )}
    </form>
  )
}

export default Login
